import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Grid, Column, Tile, TextInput, Select, SelectItem, RadioButton, RadioButtonGroup, InlineNotification } from '@carbon/react';
import { ArrowLeft, Add, Bluetooth, Scan, QrCode } from '@carbon/icons-react';
import { useTheme } from '../context/ThemeContext';
import { useZones } from '../hooks/useZones';
import { useSensorsOptimized } from '../hooks/useSensorsOptimized';
import { useSubscription } from '../hooks/useSubscription';
import { addSensor } from '../services/sensorService';
import bluetoothService from '../services/bluetoothService';
import QRCodeScanner from '../components/QRCodeScanner';
import BluetoothScanModal from '../components/BluetoothScanModal';
import SubscriptionGate from '../components/SubscriptionGate';
import { supabase } from '../services/supabaseClient';

const AddDevicePage = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { zones } = useZones();
  const { sensors } = useSensorsOptimized();
  const { canAddSensors } = useSubscription();
  const [step, setStep] = useState(1);
  const [connectionType, setConnectionType] = useState('qr');
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    zone: '',
    serialNumber: '',
    macAddress: '',
  });
  const [scanning, setScanning] = useState(false);
  const [foundDevices, setFoundDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [showBluetoothScanner, setShowBluetoothScanner] = useState(false);
  const [qrScannedData, setQrScannedData] = useState(null);
  const [bluetoothScannedData, setBluetoothScannedData] = useState(null);

  const isDark = theme === 'dark';
  const bgPrimary = isDark ? '#161616' : '#ffffff';
  const bgSecondary = isDark ? '#262626' : '#f4f4f4';
  const textPrimary = isDark ? '#f4f4f4' : '#161616';
  const textSecondary = isDark ? '#c6c6c6' : '#525252';
  const borderColor = isDark ? '#393939' : '#e0e0e0';

  const deviceTypes = [
    { value: 'kbeacon', label: 'KBeacon Pro' },
    { value: 's6', label: 'KKM S6 Sensor' },
    { value: 'ela', label: 'ELA Blue PUCK' },
    { value: 'blogm2', label: 'BLogm2 Logger' },
    { value: 'blogmp2', label: 'BLogmp2 Logger' },
  ];

  // Get available zones
  const availableZones = zones.map(zone => ({
    value: zone.id,
    label: zone.name,
  }));

  // Handle QR code scan success
  const handleQRScanSuccess = (qrData) => {
    console.log('QR Code scanned:', qrData);
    setShowQRScanner(false);

    // Parse QR code data using bluetoothService
    const parsed = bluetoothService.parseQRCodeData(qrData);
    setQrScannedData(parsed);

    // Pre-fill form data
    setFormData(prev => ({
      ...prev,
      name: parsed.sensorName,
      serialNumber: parsed.serialNumber || '',
      macAddress: parsed.macAddress || '',
      type: parsed.deviceType || prev.type,
    }));

    // Move to step 3 (configure device)
    setStep(3);
  };

  // Handle Bluetooth device selection
  const handleBluetoothDeviceSelect = (deviceData) => {
    console.log('Bluetooth device selected:', deviceData);
    setShowBluetoothScanner(false);
    setBluetoothScannedData(deviceData);

    // Pre-fill form data
    setFormData(prev => ({
      ...prev,
      name: deviceData.name || '',
      macAddress: deviceData.macAddress || '',
      serialNumber: deviceData.id || '',
      type: deviceData.deviceType || prev.type,
    }));

    // Move to step 3 (configure device)
    setStep(3);
  };

  // Handle Bluetooth scan
  const handleBluetoothScan = async () => {
    // Check if Bluetooth is available
    const availability = await bluetoothService.isBluetoothAvailable();
    if (!availability.available) {
      setError(availability.reason);
      return;
    }

    if (!bluetoothService.isChrome()) {
      setError('Bluetooth scanning works best in Chrome browser. Some features may not work in other browsers.');
      // Continue anyway, but with warning
    }

    setScanning(true);
    setFoundDevices([]);
    setError('');

    try {
      // Web Bluetooth API limitation: We can't scan continuously
      // Instead, we request device and filter results
      const device = await bluetoothService.requestDevice();
      
      if (device) {
        const deviceInfo = {
          id: device.id,
          name: device.name || 'Unknown Device',
          gatt: device.gatt,
        };

        // Filter devices - only show sensor-like devices
        if (bluetoothService.isSensorDevice(deviceInfo)) {
          // Check if device is already added
          const isAlreadyAdded = sensors.some(s => {
            const sensorMac = s.mac_address || s.device_id;
            const deviceMac = device.id;
            return sensorMac === deviceMac || s.name === deviceInfo.name;
          });

          if (!isAlreadyAdded) {
            setFoundDevices([deviceInfo]);
            setSelectedDevice(deviceInfo);
          } else {
            setError('This device is already added to your account.');
          }
        } else {
          setError('Device does not appear to be a sensor device. Please try a different device.');
        }
      }
    } catch (err) {
      console.error('Bluetooth scan error:', err);
      if (err.message === 'No device selected') {
        // User cancelled - this is okay
        setError('');
      } else {
        setError(err.message || 'Failed to scan for devices. Please try again.');
      }
    } finally {
      setScanning(false);
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!formData.name || !formData.type) {
      setError('Please fill in all required fields');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Determine device ID and MAC address based on connection type
      let deviceId = formData.serialNumber || formData.macAddress;
      let macAddress = formData.macAddress;
      let serialNumber = formData.serialNumber;

      // If QR code was scanned, use parsed data
      if (qrScannedData) {
        deviceId = qrScannedData.sensorId;
        macAddress = qrScannedData.macAddress || macAddress;
        serialNumber = qrScannedData.serialNumber || serialNumber;
      }

      // If Bluetooth device was scanned, use parsed data
      if (bluetoothScannedData) {
        deviceId = bluetoothScannedData.id;
        macAddress = bluetoothScannedData.macAddress || macAddress;
        serialNumber = bluetoothScannedData.id || serialNumber;
      }

      // If Bluetooth device was selected (legacy method), use its ID
      if (selectedDevice) {
        deviceId = selectedDevice.id;
        if (!macAddress) {
          macAddress = selectedDevice.id; // Use device ID as MAC if no MAC provided
        }
      }

      if (!deviceId) {
        setError('Device ID is required. Please enter a serial number or MAC address.');
        setIsLoading(false);
        return;
      }

      // Determine claim medium
      let claimMedium = 'Manual';
      if (qrScannedData) {
        claimMedium = 'QR Code';
      } else if (bluetoothScannedData) {
        claimMedium = 'BLE';
      } else if (connectionType === 'bluetooth') {
        claimMedium = 'BLE';
      } else if (connectionType === 'qr') {
        claimMedium = 'QR Code';
      }

      // Prepare options for addSensor (matching React Native addSensorFromQR)
      const sensorOptions = {
        deviceType: formData.type || (qrScannedData?.deviceType || undefined),
        claimMedium: claimMedium,
      };

      // For QR code claims, set advertisedName to match sensor name (same as React Native)
      if (qrScannedData) {
        sensorOptions.advertisedName = formData.name || qrScannedData.sensorName;
      }

      // Add sensor to Supabase
      await addSensor(
        deviceId,
        formData.name,
        macAddress,
        serialNumber,
        sensorOptions
      );

      // If zone is selected, assign sensor to zone
      if (formData.zone) {
        // Get the zone
        const zone = zones.find(z => z.id === formData.zone);
        if (zone) {
          // Get the sensor we just added (by device_id)
          const { data: { user } } = await supabase.auth.getUser();
          const { data: addedSensor } = await supabase
            .from('sensors')
            .select('id')
            .eq('user_id', user.id)
            .eq('device_id', deviceId)
            .single();

          if (addedSensor) {
            // Update zone to include this sensor
            const updatedSensorIds = [...(zone.sensor_ids || []), addedSensor.id];
            await supabase
              .from('zones')
              .update({ sensor_ids: updatedSensorIds })
              .eq('id', zone.id);
          }
        }
      }

      // Navigate to devices page
      navigate('/dashboard/devices');
    } catch (err) {
      console.error('Error adding device:', err);
      setError(err.message || 'Failed to add device. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SubscriptionGate 
      feature="adding devices" 
      disabled={!canAddSensors}
      customMessage="You need an active subscription to add devices to your cold chain monitoring system."
    >
      <div style={{ backgroundColor: bgPrimary, minHeight: '100vh', padding: '48px 0' }} data-testid="add-device-page">
      <Grid>
        <Column lg={{ span: 10, offset: 3 }} md={8} sm={4}>
          <Button
            kind="ghost"
            renderIcon={ArrowLeft}
            onClick={() => navigate('/dashboard/devices')}
            style={{ marginBottom: '24px' }}
            data-testid="back-button"
          >
            Back to Devices
          </Button>
          <h1 style={{ color: textPrimary, fontSize: '2rem', fontWeight: 600, marginBottom: '32px' }}>Add New Device</h1>
          {error && (
            <InlineNotification
              kind="error"
              title="Error"
              subtitle={error}
              onClose={() => setError('')}
              style={{ marginBottom: '24px' }}
            />
          )}

          {/* Step 1: Connection Type */}
          {step === 1 && (
            <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '32px', overflow: 'visible' }}>
              <h2 style={{ color: textPrimary, fontSize: '1.25rem', fontWeight: 600, marginBottom: '24px' }}>
                Step 1: Select Connection Type
              </h2>
              <div style={{ marginBottom: '32px' }}>
                <style>{`
                  .cds--radio-button-wrapper {
                    align-items: flex-start !important;
                    margin-bottom: 20px !important;
                  }
                  .cds--radio-button-wrapper:last-child {
                    margin-bottom: 0 !important;
                  }
                  .cds--radio-button-wrapper .cds--radio-button__label {
                    display: flex;
                    align-items: flex-start;
                    padding-top: 0;
                  }
                `}</style>
                <RadioButtonGroup
                  name="connection-type"
                  valueSelected={connectionType}
                  onChange={(value) => setConnectionType(value)}
                  orientation="vertical"
                >
                  <RadioButton
                    id="qr"
                    value="qr"
                    labelText={
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', lineHeight: '1.5', marginTop: '-2px' }}>
                        <QrCode size={20} style={{ color: '#24a148', flexShrink: 0, marginTop: '1px' }} />
                        <div style={{ flex: 1 }}>
                          <p style={{ color: textPrimary, fontWeight: 500, margin: 0, lineHeight: '1.5' }}>QR Code</p>
                          <p style={{ color: textSecondary, fontSize: '0.75rem', margin: 0, lineHeight: '1.4' }}>Scan device QR code</p>
                        </div>
                      </div>
                    }
                  />
                  <RadioButton
                    id="bluetooth"
                    value="bluetooth"
                    labelText={
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', lineHeight: '1.5', marginTop: '-2px' }}>
                        <Bluetooth size={20} style={{ color: '#0f62fe', flexShrink: 0, marginTop: '1px' }} />
                        <div style={{ flex: 1 }}>
                          <p style={{ color: textPrimary, fontWeight: 500, margin: 0, lineHeight: '1.5' }}>Bluetooth</p>
                          <p style={{ color: textSecondary, fontSize: '0.75rem', margin: 0, lineHeight: '1.4' }}>Scan for nearby Bluetooth devices (Chrome only)</p>
                        </div>
                      </div>
                    }
                  />
                  <RadioButton
                    id="manual"
                    value="manual"
                    labelText={
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', lineHeight: '1.5', marginTop: '-2px' }}>
                        <Add size={20} style={{ color: '#6929c4', flexShrink: 0, marginTop: '1px' }} />
                        <div style={{ flex: 1 }}>
                          <p style={{ color: textPrimary, fontWeight: 500, margin: 0, lineHeight: '1.5' }}>Manual Entry</p>
                          <p style={{ color: textSecondary, fontSize: '0.75rem', margin: 0, lineHeight: '1.4' }}>Enter device details manually</p>
                        </div>
                      </div>
                    }
                  />
                </RadioButtonGroup>
              </div>
              <Button kind="primary" onClick={() => {
                if (connectionType === 'qr') {
                  setShowQRScanner(true);
                } else if (connectionType === 'bluetooth') {
                  setShowBluetoothScanner(true);
                } else {
                  setStep(2);
                }
              }} data-testid="next-step">
                Continue
              </Button>
            </Tile>
          )}

          {/* Step 2: Bluetooth Scan or Manual Entry */}
          {step === 2 && connectionType === 'bluetooth' && (
            <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '32px' }}>
              <h2 style={{ color: textPrimary, fontSize: '1.25rem', fontWeight: 600, marginBottom: '24px' }}>
                Step 2: Scan for Devices
              </h2>
              {!bluetoothService.isBluetoothSupported() && (
                <InlineNotification
                  kind="error"
                  title="Bluetooth Not Supported"
                  subtitle="Web Bluetooth API is not supported in this browser. Please use Chrome browser."
                  style={{ marginBottom: '24px' }}
                />
              )}
              {bluetoothService.isBluetoothSupported() && !bluetoothService.isSecureContext() && (
                <InlineNotification
                  kind="error"
                  title="HTTPS Required"
                  subtitle="Bluetooth requires a secure connection (HTTPS). Please access this page using https:// or from localhost."
                  style={{ marginBottom: '24px' }}
                />
              )}
              {bluetoothService.isBluetoothSupported() && bluetoothService.isSecureContext() && !bluetoothService.isChrome() && (
                <InlineNotification
                  kind="warning"
                  title="Browser Recommendation"
                  subtitle="Bluetooth scanning works best in Chrome browser. Some features may not work in other browsers."
                  style={{ marginBottom: '24px' }}
                />
              )}
              {bluetoothService.isBluetoothSupported() && bluetoothService.isSecureContext() && bluetoothService.isChrome() && (
                <InlineNotification
                  kind="info"
                  title="Ready to Scan"
                  subtitle="Make sure Bluetooth is enabled on your device and your sensor is nearby and powered on."
                  style={{ marginBottom: '24px' }}
                />
              )}
              <Button
                kind="primary"
                renderIcon={Scan}
                onClick={handleBluetoothScan}
                disabled={scanning || !bluetoothService.isBluetoothSupported() || !bluetoothService.isSecureContext()}
                style={{ marginBottom: '24px' }}
                data-testid="scan-button"
              >
                {scanning ? 'Scanning...' : 'Scan for Devices'}
              </Button>

              {foundDevices.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                  <p style={{ color: textSecondary, marginBottom: '12px' }}>Found {foundDevices.length} device(s):</p>
                  {foundDevices.map((device) => (
                    <Tile
                      key={device.id}
                      style={{
                        backgroundColor: selectedDevice?.id === device.id ? '#0f62fe20' : bgPrimary,
                        border: `1px solid ${selectedDevice?.id === device.id ? '#0f62fe' : borderColor}`,
                        padding: '16px',
                        marginBottom: '8px',
                        cursor: 'pointer',
                      }}
                      onClick={() => setSelectedDevice(device)}
                      data-testid={`found-device-${device.id}`}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <p style={{ color: textPrimary, fontWeight: 500 }}>{device.name}</p>
                          <p style={{ color: textSecondary, fontSize: '0.75rem' }}>ID: {device.id}</p>
                        </div>
                      </div>
                    </Tile>
                  ))}
                </div>
              )}

              <div style={{ display: 'flex', gap: '16px' }}>
                <Button kind="secondary" onClick={() => setStep(1)}>Back</Button>
                <Button kind="primary" onClick={() => setStep(3)} disabled={!selectedDevice}>Continue</Button>
              </div>
            </Tile>
          )}

          {step === 2 && connectionType === 'manual' && (
            <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '32px' }}>
              <h2 style={{ color: textPrimary, fontSize: '1.25rem', fontWeight: 600, marginBottom: '24px' }}>
                Step 2: Enter Device Details
              </h2>
              <div style={{ marginBottom: '24px' }}>
                <TextInput
                  id="serialNumber"
                  labelText="Serial Number / MAC Address *"
                  placeholder="Enter device serial number or MAC address"
                  value={formData.serialNumber}
                  onChange={(e) => setFormData({ ...formData, serialNumber: e.target.value })}
                  data-testid="serial-number"
                />
              </div>
              <div style={{ display: 'flex', gap: '16px' }}>
                <Button kind="secondary" onClick={() => setStep(1)}>Back</Button>
                <Button kind="primary" onClick={() => setStep(3)} disabled={!formData.serialNumber}>Continue</Button>
              </div>
            </Tile>
          )}

          {/* Step 3: Configure Device */}
          {step === 3 && (
            <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '32px' }}>
              <h2 style={{ color: textPrimary, fontSize: '1.25rem', fontWeight: 600, marginBottom: '24px' }}>
                Step 3: Configure Device
              </h2>
              <div style={{ marginBottom: '24px' }}>
                <TextInput
                  id="name"
                  labelText="Device Name *"
                  placeholder="e.g., Freezer Sensor #1"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="device-name"
                />
              </div>
              <div style={{ marginBottom: '24px' }}>
                <Select
                  id="type"
                  labelText="Device Type *"
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  data-testid="device-type"
                >
                  <SelectItem value="" text="Select device type" />
                  {deviceTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value} text={type.label} />
                  ))}
                </Select>
              </div>
              {availableZones.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                  <Select
                    id="zone"
                    labelText="Assign to Zone (Optional)"
                    value={formData.zone}
                    onChange={(e) => setFormData({ ...formData, zone: e.target.value })}
                    data-testid="device-zone"
                  >
                    <SelectItem value="" text="No zone" />
                    {availableZones.map((zone) => (
                      <SelectItem key={zone.value} value={zone.value} text={zone.label} />
                    ))}
                  </Select>
                </div>
              )}
              {formData.macAddress && (
                <div style={{ marginBottom: '24px' }}>
                  <TextInput
                    id="macAddress"
                    labelText="MAC Address"
                    value={formData.macAddress}
                    onChange={(e) => setFormData({ ...formData, macAddress: e.target.value })}
                    disabled
                  />
                </div>
              )}
              <div style={{ display: 'flex', gap: '16px' }}>
                <Button kind="secondary" onClick={() => {
                  setStep(connectionType === 'manual' ? 2 : 1);
                  setQrScannedData(null);
                  setBluetoothScannedData(null);
                }}>Back</Button>
                <Button 
                  kind="primary" 
                  renderIcon={Add} 
                  onClick={handleSubmit} 
                  disabled={!formData.name || !formData.type || isLoading}
                  data-testid="add-device-submit"
                >
                  {isLoading ? 'Adding...' : 'Add Device'}
                </Button>
              </div>
            </Tile>
          )}
        </Column>
      </Grid>

      {/* QR Code Scanner Modal */}
      <QRCodeScanner
        isOpen={showQRScanner}
        onClose={() => setShowQRScanner(false)}
        onScanSuccess={handleQRScanSuccess}
      />

      {/* Bluetooth Scanner Modal */}
      <BluetoothScanModal
        isOpen={showBluetoothScanner}
        onClose={() => setShowBluetoothScanner(false)}
        onDeviceSelect={handleBluetoothDeviceSelect}
      />
    </div>
    </SubscriptionGate>
  );
};

export default AddDevicePage;
